import React, { useEffect, useState } from "react";
import { api } from "../lib/auth";

export default function MasterLicencesPage() {
  const [masterLicences, setMasterLicences] = useState([]);
  const [masterLicenceForm, setMasterLicenceForm] = useState({
    company_name: "",
    master_licence_number: "",
    state_of_issue: "NSW",
    issue_date: "",
    expiry_date: "",
    notes: "",
    file: null,
  });
  const [loading, setLoading] = useState(false);

  const fetchMasterLicences = async () => {
    try {
      const res = await api.get("/personnel-compliance/master-licences");
      const rows = Array.isArray(res.data) ? res.data : [];
      setMasterLicences([...rows]);
    } catch (err) {
      console.error("Failed to fetch master licences", err);
      setMasterLicences([]);
    }
  };

  useEffect(() => {
    fetchMasterLicences();
  }, []);

  const uploadMasterLicence = async (e) => {
    e.preventDefault();

    if (!masterLicenceForm.file) {
      alert("Please select a Master Licence document to upload.");
      return;
    }

    const fd = new FormData();
    fd.append("company_name", masterLicenceForm.company_name);
    fd.append("master_licence_number", masterLicenceForm.master_licence_number);
    fd.append("state_of_issue", masterLicenceForm.state_of_issue || "NSW");
    fd.append("issue_date", masterLicenceForm.issue_date || "");
    fd.append("expiry_date", masterLicenceForm.expiry_date);
    fd.append("notes", masterLicenceForm.notes || "");
    fd.append("file", masterLicenceForm.file);

    try {
      setLoading(true);

      await api.post("/personnel-compliance/master-licences", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMasterLicenceForm({
        company_name: "",
        master_licence_number: "",
        state_of_issue: "NSW",
        issue_date: "",
        expiry_date: "",
        notes: "",
        file: null,
      });

      await fetchMasterLicences();
      alert("Master Licence uploaded successfully.");
    } catch (err) {
      console.error("Failed to upload Master Licence", err);
      alert(err?.response?.data?.detail || "Failed to upload Master Licence.");
    } finally {
      setLoading(false);
    }
  };

  const deleteMasterLicence = async (licence) => {
    const licenceId = licence?.id || licence?._id;

    if (!licenceId) {
      alert("Unable to delete Master Licence: missing record ID.");
      return;
    }

    if (!window.confirm("Delete this Master Licence record?")) return;

    try {
      await api.delete(`/personnel-compliance/master-licences/${licenceId}`);
      setMasterLicences((prev) =>
        prev.filter((item) => String(item.id || item._id) !== String(licenceId))
      );
      await fetchMasterLicences();
    } catch (err) {
      console.error("Failed to delete Master Licence", err);
      alert(err?.response?.data?.detail || "Failed to delete Master Licence.");
    }
  };

  const daysLeft = (expiryDate) => {
    if (!expiryDate) return "-";
    return Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const fileUrl = (lic) => {
    const licenceId = lic?.id || lic?._id;
    if (!licenceId) return "";

    const version = lic.updated_at || lic.created_at || Date.now();

    return `/api/personnel-compliance/master-licences/${licenceId}/document?v=${encodeURIComponent(version)}`;
  };

  return (
    <div className="rx-master-licence-page space-y-6 p-6 pb-24">
      <div>
        <h1 className="rx-page-title text-3xl font-bold">Master Licence Register</h1>
        <p className="rx-page-subtitle mt-1">
          Company-wide security Master Licence management and compliance tracking.
        </p>
      </div>

      <div className="master-licence-card rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-xl">
        <form onSubmit={uploadMasterLicence} className="grid grid-cols-1 gap-3 md:grid-cols-3 mb-6">
          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Company Name"
            value={masterLicenceForm.company_name}
            required
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, company_name: e.target.value })}
          />

          <input
            className="border rounded-lg px-3 py-2"
            placeholder="Master Licence Number"
            value={masterLicenceForm.master_licence_number}
            required
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, master_licence_number: e.target.value })}
          />

          <input
            className="border rounded-lg px-3 py-2"
            placeholder="State of Issue"
            value={masterLicenceForm.state_of_issue}
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, state_of_issue: e.target.value })}
          />

          <input
            type="date"
            className="border rounded-lg px-3 py-2"
            value={masterLicenceForm.expiry_date}
            required
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, expiry_date: e.target.value })}
          />

          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="border rounded-lg px-3 py-2"
            required
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, file: e.target.files?.[0] || null })}
          />

          <textarea
            className="border rounded-lg px-3 py-2 md:col-span-2"
            placeholder="Notes"
            value={masterLicenceForm.notes}
            onChange={(e) => setMasterLicenceForm({ ...masterLicenceForm, notes: e.target.value })}
          />

          <button
            type="submit"
            disabled={loading}
            className="rx-master-upload rounded-lg px-4 py-2 font-semibold"
          >
            {loading ? "Uploading..." : "Upload Master Licence"}
          </button>
        </form>

        <div className="overflow-x-auto rounded-xl border border-slate-700">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className="text-left p-2">Company</th>
                <th className="text-left p-2">Licence No.</th>
                <th className="text-left p-2">State</th>
                <th className="text-left p-2">Expiry Date</th>
                <th className="text-left p-2">Days Left</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Document</th>
                <th className="text-right p-2">Action</th>
              </tr>
            </thead>

            <tbody>
              {(masterLicences || []).filter(Boolean).map((lic, idx) => (
                <tr key={lic.id || lic._id || idx} className="border-t">
                  <td className="p-2">{lic.company_name}</td>
                  <td className="p-2">{lic.master_licence_number}</td>
                  <td className="p-2">{lic.state_of_issue}</td>
                  <td className="p-2">{lic.expiry_date}</td>
                  <td className="p-2">{daysLeft(lic.expiry_date)}</td>
                  <td className="p-2">{lic.verification_status || "Pending"}</td>
                  <td className="p-2">
                    {fileUrl(lic) ? (
                      <a
                        href={fileUrl(lic)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-400 underline font-semibold"
                      >
                        View Document
                      </a>
                    ) : (
                      <span className="text-red-400">Missing File</span>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    <button
                      type="button"
                      onClick={() => deleteMasterLicence(lic)}
                      className="text-red-400 font-semibold"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}

              {(!masterLicences || masterLicences.length === 0) && (
                <tr>
                  <td className="p-4 text-center text-slate-300" colSpan="8">
                    No Master Licence documents uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
